import { FilterBar } from '@ctw/shared/components/filter-bar/filter-bar';
import type { FilterBarProps } from '@ctw/shared/components/filter-bar/filter-bar-types';
import { SortButton } from '@ctw/shared/components/sort-button/sort-button';
import type { LegacyMinRecordItem } from '@ctw/shared/components/tables/legacy-table';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import type { Sort } from '@ctw/shared/utils/sort';
import { type ClassName, tw, twx } from '@ctw/shared/utils/tailwind';
import type { NonEmptyArray } from '@ctw/shared/utils/types';
import type { ReactNode } from 'react';
import { ViewButton, type ViewButtonProps, ViewButtonsExpanded } from './view-button';

export type ResourceTableSortOption<T extends object> = {
  display: string;
  sorts: NonEmptyArray<Sort<T>>;
};

type ResourceTableSortButtonProps<T extends object> = {
  className?: ClassName;
  defaultSort: ResourceTableSortOption<T>;
  onChange: (sort: ResourceTableSortOption<T>) => void;
  options: Array<ResourceTableSortOption<T>>;
};

const ResourceTableSortButton = <T extends object>({
  className,
  defaultSort,
  onChange,
  options,
}: ResourceTableSortButtonProps<T>) => {
  const { trackInteraction } = useTelemetry();
  const sortButtonOptions = options.map((opt) => opt.display);

  return (
    <SortButton<ResourceTableSortOption<T>['display']>
      className={twx(className)}
      defaultSort={defaultSort.display}
      options={sortButtonOptions}
      onChange={(selectedDisplay) => {
        const selectedOption = options.find((opt) => opt.display === selectedDisplay);
        if (selectedOption) {
          trackInteraction('sort_change', {
            sort: selectedOption.display,
            sortDirection: selectedOption.sorts[0].dir,
          });
          onChange(selectedOption);
        }
      }}
    />
  );
};

export type ResourceTableActionsProps<T extends LegacyMinRecordItem> = {
  headAction?: ReactNode;
  action?: ReactNode;
  className?: ClassName;
  filterOptions?: FilterBarProps;
  sortOptions?: ResourceTableSortButtonProps<T>;
  viewOptions?: ViewButtonProps<T> & { isButtonGroup?: boolean };
};

export const ResourceTableActions = <T extends LegacyMinRecordItem>({
  action,
  className,
  filterOptions,
  headAction,
  sortOptions,
  viewOptions,
}: ResourceTableActionsProps<T>) => {
  const isEmpty = !(viewOptions || sortOptions || filterOptions || action);
  if (isEmpty) {
    return null;
  }

  return (
    <div className={twx(className, 'flex flex-wrap')}>
      <div className={tw`flex w-full flex-wrap items-center gap-2`}>
        {headAction && <div className={tw`whitespace-nowrap`}>{headAction}</div>}
        {viewOptions && !viewOptions?.isButtonGroup && (
          <ViewButton
            className={twx(viewOptions.className)}
            defaultView={viewOptions.defaultView}
            onChange={viewOptions.onChange}
            options={viewOptions.options}
          />
        )}
        {sortOptions && (
          <ResourceTableSortButton
            className={twx(sortOptions.className)}
            defaultSort={sortOptions.defaultSort}
            onChange={sortOptions.onChange}
            options={sortOptions.options}
          />
        )}
        {filterOptions && filterOptions.filters.length > 0 && <FilterBar {...filterOptions} />}
        {viewOptions?.isButtonGroup && (
          <ViewButtonsExpanded
            defaultView={viewOptions.defaultView}
            onChange={viewOptions.onChange}
            options={viewOptions.options}
          />
        )}
      </div>
      <div className={tw`whitespace-nowrap`}>{action}</div>
    </div>
  );
};
